// Deterministic concentric layout (pure). Decisions sit at the center; entities
// are placed on rings by their hop-distance from the nearest decision, so the graph
// reads "outward from the decisions". No physics sim, no randomness -> stable across
// renders and unit-testable.
import { buildAdjacency } from "./brainGraphAdapter.js";

function bfsHops(nodes, adjacency) {
  const hop = new Map();
  const queue = [];
  for (const n of nodes) {
    if (n.cluster === "DECISION") {
      hop.set(n.id, 0);
      queue.push(n.id);
    }
  }
  // fallback: if there are no decision seeds, seed from every node at hop 0.
  if (queue.length === 0) {
    for (const n of nodes) {
      hop.set(n.id, 0);
      queue.push(n.id);
    }
  }
  while (queue.length) {
    const cur = queue.shift();
    const d = hop.get(cur);
    for (const nb of adjacency.get(cur) || []) {
      if (!hop.has(nb)) {
        hop.set(nb, d + 1);
        queue.push(nb);
      }
    }
  }
  let maxHop = 0;
  for (const n of nodes) {
    if (!hop.has(n.id)) hop.set(n.id, 1); // disconnected -> ring 1
    maxHop = Math.max(maxHop, hop.get(n.id));
  }
  return { hop, maxHop };
}

export function layoutGraph(graph, { width = 900, height = 600, ringGap } = {}) {
  const nodes = graph.nodes || [];
  const adjacency = buildAdjacency(graph.edges || []);
  const { hop, maxHop } = bfsHops(nodes, adjacency);

  const cx = width / 2;
  const cy = height / 2;
  const rGap = ringGap || Math.max(70, Math.min(cx, cy) / (maxHop + 1));

  // group by hop, stable order (input order)
  const rings = new Map();
  for (const n of nodes) {
    const h = hop.get(n.id);
    if (!rings.has(h)) rings.set(h, []);
    rings.get(h).push(n);
  }

  const pos = new Map();
  const angleOf = new Map();

  const ringHops = Array.from(rings.keys()).sort((a, b) => a - b);
  for (const h of ringHops) {
    const ringNodes = rings.get(h);
    if (h === 0) {
      if (ringNodes.length === 1) {
        pos.set(ringNodes[0].id, { x: cx, y: cy });
        angleOf.set(ringNodes[0].id, 0);
      } else {
        ringNodes.forEach((n, i) => {
          const a = (2 * Math.PI * i) / ringNodes.length;
          const r = rGap * 0.5;
          pos.set(n.id, { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
          angleOf.set(n.id, a);
        });
      }
      continue;
    }
    // candidate angle inherited from placed neighbors (locality), then evenly
    // redistributed in candidate-angle order to guarantee no overlap.
    const withAngle = ringNodes.map((n, idx) => {
      const neigh = (adjacency.get(n.id) || []).filter((m) => angleOf.has(m));
      let a;
      if (neigh.length) {
        const xs = neigh.map((m) => Math.cos(angleOf.get(m)));
        const ys = neigh.map((m) => Math.sin(angleOf.get(m)));
        a = Math.atan2(ys.reduce((s, v) => s + v, 0), xs.reduce((s, v) => s + v, 0));
      } else {
        a = (2 * Math.PI * idx) / ringNodes.length;
      }
      return { n, a: (a + 2 * Math.PI) % (2 * Math.PI), idx };
    });
    withAngle.sort((p, q) => (p.a === q.a ? p.idx - q.idx : p.a - q.a));
    const r = rGap * h;
    withAngle.forEach((item, i) => {
      const a = (2 * Math.PI * i) / withAngle.length;
      pos.set(item.n.id, { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
      angleOf.set(item.n.id, a);
    });
  }

  return { positions: pos, hop, maxHop, width, height };
}
